/**
 * Focus-mode table items: pure derivation from chartId + ChartRenderProps.
 * Used by Charts.tsx renderFocusChart; filtering/sorting for ranked charts stays in the page.
 */

import React from 'react';
import { toActivitiesUrl, toAnalyticsUrl } from '@/components/visuals';
import type { TopItem } from '@/components/visuals';
import { BUCKET_TO_FLOAT_RANGE } from './helpers';
import type { ChartRenderProps } from './chartRenderers';
import type { UnitsSpreadBasis } from '@/utils/resolvePlannedUnitsSeries';
import { resolvePlannedUnitsSeries } from '@/utils/resolvePlannedUnitsSeries';

const BUCKET_LABELS: Record<string, string> = {
  '<0': '< 0', '0': '0', '1-7': '1–7', '8-14': '8–14', '15-30': '15–30', '31-44': '31–44', '>44': '> 44',
};

/**
 * Pure helper: chart + empty-state table block (message only). Used when chart has no data in focus mode.
 */
export function emptyAndTable(chart: React.ReactNode, message: string): React.ReactNode {
  return (
    <>
      {chart}
      <div className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-2 px-1 pb-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
          <span className="w-5 text-right">#</span>
          <span className="flex-1">Name</span>
          <span className="w-16 text-right">Value</span>
        </div>
        <p className="px-1 py-2">{message}</p>
      </div>
    </>
  );
}

/**
 * Build focus table items for a chart from chartId and render props.
 * For ranked charts (wbs_hotspots, milestone_variance) the caller passes already filtered/sorted arrays in props.
 */
export function getFocusItems(chartId: string, props: ChartRenderProps): TopItem[] {
  const {
    floatDist,
    criticalCount,
    nearCriticalCount,
    monitorCount,
    wbsHotspots,
    milestonesWithVariance,
    hasBaselineData,
    qualityChecks,
    scheduleAnalytics: scheduleTyped,
    trendsData,
  } = props;

  if (chartId === 'wbs_hotspots') {
    return wbsHotspots.map((w) => ({
      id: w.wbs_id,
      label: w.wbs_name || w.wbs_short_name || w.wbs_id,
      value: w.hotspotScore,
      href: toActivitiesUrl({ wbs_id: w.wbs_id, wbs_code: w.wbs_short_name || w.wbs_id }),
    }));
  }

  if (chartId === 'milestone_variance' && hasBaselineData) {
    return milestonesWithVariance.map((m) => ({
      id: m.id,
      label: m.name || '—',
      value: m.varianceDays ?? 0,
      href: toAnalyticsUrl('milestones'),
    }));
  }

  if (chartId === 'quality_issues') {
    return qualityChecks.map((q) => ({
      id: q.id,
      label: q.label,
      value: q.value,
      href: toAnalyticsUrl('quality'),
    }));
  }

  if (chartId === 'float_distribution') {
    return floatDist.map((d) => {
      const href =
        d.bucket === '<0'
          ? toActivitiesUrl({ negativeFloat: true })
          : d.bucket === '0'
            ? toActivitiesUrl({ critical: true })
            : (() => {
                const range = BUCKET_TO_FLOAT_RANGE[d.bucket];
                return range ? toActivitiesUrl({ float_min: range.min, float_max: range.max }) : '/app/activities';
              })();
      return {
        id: d.bucket,
        label: BUCKET_LABELS[d.bucket] ?? d.bucket,
        value: d.count,
        href,
      };
    });
  }

  if (chartId === 'criticality_breakdown') {
    return [
      { id: 'critical', label: 'Critical', value: criticalCount, href: toActivitiesUrl({ critical: true }) },
      { id: 'near-critical', label: 'Near-critical', value: nearCriticalCount, href: toAnalyticsUrl('critical-path') },
      { id: 'monitor', label: 'Monitor', value: monitorCount, href: toAnalyticsUrl('critical-path') },
    ].filter((row) => row.value > 0);
  }

  if (chartId === 's_curve_activities') {
    const raw = scheduleTyped?.s_curve_activities;
    const planned = raw?.planned ?? [];
    const actual = raw?.actual ?? [];
    const lastPlanned = planned.length > 0 ? planned[planned.length - 1]?.cumulative ?? 0 : 0;
    const lastActual = actual.length > 0 ? actual[actual.length - 1]?.cumulative ?? 0 : 0;
    return [
      { id: 'planned', label: 'Planned (cumulative)', value: lastPlanned, href: '/app/activities' },
      { id: 'actual', label: 'Actual (cumulative)', value: lastActual, href: '/app/activities' },
    ];
  }

  if (chartId === 's_curve_duration') {
    const raw = scheduleTyped?.s_curve_duration;
    const planned = raw?.planned ?? [];
    const earned = raw?.earned ?? [];
    const lastPlanned = planned.length > 0 ? planned[planned.length - 1]?.cumulative ?? 0 : 0;
    const lastEarned = earned.length > 0 ? earned[earned.length - 1]?.cumulative ?? 0 : 0;
    return [
      { id: 'planned', label: 'Planned (cumulative hrs)', value: lastPlanned, href: '/app/activities' },
      { id: 'earned', label: 'Earned (cumulative hrs)', value: lastEarned, href: '/app/activities' },
    ];
  }

  if (chartId === 's_curve_units') {
    const raw = scheduleTyped?.s_curve_units;
    const planned = raw?.planned ?? [];
    const earned = raw?.earned ?? [];
    if (planned.length === 0 && earned.length === 0) return [];
    const lastPlanned = planned.length > 0 ? planned[planned.length - 1]?.cumulative ?? 0 : 0;
    const lastEarned = earned.length > 0 ? earned[earned.length - 1]?.cumulative ?? 0 : 0;
    return [
      { id: 'planned', label: 'Planned (cumulative units)', value: lastPlanned, href: '/app/activities' },
      { id: 'earned', label: 'Earned (cumulative units)', value: lastEarned, href: '/app/activities' },
    ];
  }

  if (chartId === 'units_per_day_histogram' || chartId === 'planned_units_s_curve') {
    const puEngine = Boolean(scheduleTyped?.planned_units_time_phased?.engineEnabled);
    const spread: UnitsSpreadBasis = (props.unitsSpreadBasis ??
      (puEngine ? 'working' : 'calendar')) as UnitsSpreadBasis;
    const { series, totalPlannedUnits } = resolvePlannedUnitsSeries(
      props.activities ?? [],
      {
        dateBasis: props.scheduleDateBasis ?? 'baseline',
        includeMilestones: props.includeMilestoneUnits ?? false,
      },
      scheduleTyped,
      spread
    );
    if (series.length === 0) return [];
    const peak = series.reduce((m, p) => Math.max(m, p.unitsPerDay), 0);
    let peakLabel = 'Peak planned units/day';
    if (peak > 0) {
      const at = series.find((p) => p.unitsPerDay >= peak);
      if (at?.date) peakLabel = `Peak (${at.date})`;
    }
    const last = series[series.length - 1];
    const rows =
      chartId === 'planned_units_s_curve'
        ? [
            { id: 'total', label: 'Total planned units (included)', value: totalPlannedUnits, href: '/app/activities' },
            {
              id: 'cumulative-end',
              label: 'Cumulative units (end of timeline)',
              value: last.cumulativeUnits,
              href: '/app/activities',
            },
          ]
        : [
            { id: 'total', label: 'Total planned units (included)', value: totalPlannedUnits, href: '/app/activities' },
            { id: 'peak', label: peakLabel, value: peak, href: '/app/activities' },
          ];
    return rows;
  }

  if (chartId === 's_curve_wbs') {
    const firstWbs = scheduleTyped?.s_curve_wbs?.series?.[0];
    const points = firstWbs?.points ?? [];
    const lastPlanned = points.length > 0 ? (points[points.length - 1]?.planned ?? 0) : 0;
    const lastEarned = points.length > 0 ? (points[points.length - 1]?.earned ?? 0) : 0;
    return [
      { id: 'planned', label: 'Planned (hrs)', value: lastPlanned, href: '/app/activities' },
      { id: 'earned', label: 'Earned (hrs)', value: lastEarned, href: '/app/activities' },
    ];
  }

  if (chartId === 'milestone_on_time') {
    const periods = scheduleTyped?.milestone_on_time?.periods ?? [];
    return periods.map((p, i) => ({
      id: String(i),
      label: p.period ?? '—',
      value: p.on_time_pct ?? 0,
      href: toAnalyticsUrl('milestones'),
    }));
  }

  if (chartId === 'resource_demand_capacity') {
    const raw = scheduleTyped?.resource_demand_capacity;
    const demand = raw?.demand ?? [];
    const spreadLabel =
      raw?.spread_basis === 'working_calendar'
        ? 'Working calendar spread'
        : raw?.spread_basis === 'proportional_legacy'
          ? 'Legacy proportional spread'
          : 'Demand spread';
    const items: TopItem[] = [
      {
        id: 'demand',
        label: `Demand (${spreadLabel})`,
        value: demand.reduce((a, b) => a + b, 0),
        href: toAnalyticsUrl('health'),
      },
    ];
    if (raw?.capacity_available && Array.isArray(raw.capacity) && raw.capacity.length > 0) {
      items.push({
        id: 'capacity',
        label: 'Capacity (total)',
        value: (raw.capacity as number[]).reduce((a, b) => a + b, 0),
        href: toAnalyticsUrl('health'),
      });
    }
    return items;
  }

  if (
    chartId === 'critical_path_length' ||
    chartId === 'float_trend' ||
    chartId === 'near_critical_trend' ||
    chartId === 'logic_integrity_trend' ||
    chartId === 'phase_progress_burnup'
  ) {
    const points = trendsData?.points ?? [];
    if (points.length === 0) return [];
    const last = points[points.length - 1];
    const metrics = last?.metrics ?? {};
    const summaryItems: TopItem[] = [];
    if (chartId === 'critical_path_length') {
      summaryItems.push({
        id: 'cp',
        label: 'Critical path (days)',
        value: (metrics.critical_path_days as number) ?? 0,
        href: toAnalyticsUrl('critical-path'),
      });
    } else if (chartId === 'float_trend') {
      summaryItems.push({ id: 'avg', label: 'Avg float (days)', value: (metrics.avg_float_days as number) ?? 0, href: '/app/activities' });
      summaryItems.push({ id: 'min', label: 'Min float (days)', value: (metrics.min_float_days as number) ?? 0, href: '/app/activities' });
    } else if (chartId === 'near_critical_trend') {
      summaryItems.push({
        id: 'nc',
        label: 'Near-critical count',
        value: (metrics.near_critical_count as number) ?? 0,
        href: '/app/activities',
      });
    } else if (chartId === 'logic_integrity_trend') {
      summaryItems.push({ id: 'pred', label: 'Missing pred', value: (metrics.missing_pred as number) ?? 0, href: toAnalyticsUrl('quality') });
      summaryItems.push({ id: 'succ', label: 'Missing succ', value: (metrics.missing_succ as number) ?? 0, href: toAnalyticsUrl('quality') });
      summaryItems.push({ id: 'constrained', label: 'Constrained', value: (metrics.constrained_count as number) ?? 0, href: toAnalyticsUrl('quality') });
    } else {
      const phases = (metrics.phases as Array<{ wbs_name?: string; pct_complete?: number }>) ?? [];
      phases.forEach((ph, i) => {
        summaryItems.push({
          id: String(i),
          label: ph.wbs_name ?? '—',
          value: ph.pct_complete ?? 0,
          href: '/app/activities',
        });
      });
      if (summaryItems.length === 0) {
        summaryItems.push({ id: '0', label: 'Phase %', value: 0, href: '/app/activities' });
      }
    }
    return summaryItems;
  }

  return [];
}
